// src/components/BoostPlans/BoostPlans.js
import React, { useState, useEffect } from 'react'
import { useNavigate } from 'react-router-dom'
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Grid,
  Card,
  CardContent,
  Typography,
  Box,
  Chip,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  Alert,
  CircularProgress,
  alpha
} from '@mui/material'
import {
  TrendingUpRounded,
  StarRounded,
  CheckCircleRounded,
  CloseRounded,
  RocketLaunchRounded
} from '@mui/icons-material'
import axios from 'axios'
import { API_BASE_URL } from '../../config'

const BoostPlans = ({ open, onClose, property, onBoostCreated }) => {
  const navigate = useNavigate()
  const [plans, setPlans] = useState([])
  const [loading, setLoading] = useState(false)
  const [creating, setCreating] = useState(false)
  const [error, setError] = useState('')

  useEffect(() => {
    if (open) {
      loadPlans()
    }
  }, [open])

  const loadPlans = async () => {
    setLoading(true)
    setError('')

    try {
      const response = await axios.get(`${API_BASE_URL}/api/boost/plans/`)

      let plansData = []
      if (response.data.results && Array.isArray(response.data.results)) {
        plansData = response.data.results
      } else if (Array.isArray(response.data)) {
        plansData = response.data
      }

      if (plansData.length > 0) {
        setPlans(plansData)
      } else {
        setPlans([])
        setError('Nenhum plano disponível')
      }
    } catch (error) {
      console.error('Erro ao carregar planos:', error)
      setPlans([])
      setError('Erro ao carregar planos de impulsionamento')
    } finally {
      setLoading(false)
    }
  }

  const handleCreateBoost = (plan) => {
    // Fecha o modal
    onClose()

    // Redireciona para a página de impulsionamento
    // que irá criar o pagamento com o plano selecionado
    navigate(`/impulsionar/${property.id}`, {
      state: {
        selectedPlanId: plan.id
      }
    })
  }

  const formatPrice = (price) => {
    return new Intl.NumberFormat('pt-BR', {
      style: 'currency',
      currency: 'BRL'
    }).format(price)
  }

  const getPlanIcon = (planType) => {
    switch (planType) {
      case 'basic':
        return <TrendingUpRounded sx={{ fontSize: 40 }} />
      case 'premium':
        return <StarRounded sx={{ fontSize: 40 }} />
      case 'super':
        return <RocketLaunchRounded sx={{ fontSize: 40 }} />
      default:
        return <TrendingUpRounded sx={{ fontSize: 40 }} />
    }
  }

  const getPlanColor = (planType) => {
    switch (planType) {
      case 'basic':
        return '#2196F3'
      case 'premium':
        return '#FF9800'
      case 'super':
        return '#F44336'
      default:
        return '#2196F3'
    }
  }

  if (loading) {
    return (
      <Dialog open={open} onClose={onClose} maxWidth='sm'>
        <DialogContent sx={{ p: 4, textAlign: 'center' }}>
          <CircularProgress />
          <Typography variant='body1' sx={{ mt: 2 }}>
            Carregando planos...
          </Typography>
        </DialogContent>
      </Dialog>
    )
  }

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth='lg'
      fullWidth
      PaperProps={{
        sx: {
          borderRadius: 2,
          maxHeight: '90vh'
        }
      }}
    >
      <DialogTitle
        sx={{
          textAlign: 'center',
          pb: 2,
          pt: 3,
          px: 3
        }}
      >
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            gap: 1,
            mb: 1
          }}
        >
          <RocketLaunchRounded color='primary' sx={{ fontSize: 32 }} />
          <Typography variant='h5' component='div' fontWeight='bold'>
            Impulsionar Imóvel
          </Typography>
        </Box>
        <Typography
          variant='body2'
          color='text.secondary'
          sx={{
            display: 'block',
            maxWidth: '100%',
            wordBreak: 'break-word',
            lineHeight: 1.4
          }}
        >
          {property?.title}
        </Typography>
      </DialogTitle>

      <DialogContent
        sx={{
          px: 3,
          pb: 2,
          pt: 4,
          minWidth: 850,
          overflow: 'visible'
        }}
      >
        {error && (
          <Alert severity='error' sx={{ mb: 2 }}>
            {error}
          </Alert>
        )}

        <Grid container spacing={2} wrap='nowrap' mb={4}>
          {Array.isArray(plans) && plans.length > 0 ? (
            plans.map((plan) => (
              <Grid
                size={{
                  xs: 12
                }}
                key={plan.id}
              >
                <Card
                  sx={{
                    height: '100%',
                    display: 'flex',
                    flexDirection: 'column',
                    border: 2,
                    borderColor:
                      plan.plan_type === 'premium'
                        ? getPlanColor(plan.plan_type)
                        : 'divider',
                    position: 'relative',
                    transition: 'all 0.3s ease',
                    mt: 4,
                    '&:hover': {
                      transform: 'translateY(-4px)',
                      boxShadow: `0 8px 24px ${alpha(getPlanColor(plan.plan_type), 0.25)}`
                    }
                  }}
                >
                  {plan.plan_type === 'premium' && (
                    <Box
                      sx={{
                        position: 'absolute',
                        top: -16,
                        left: '50%',
                        transform: 'translateX(-50%)',
                        bgcolor: getPlanColor(plan.plan_type),
                        color: 'white',
                        px: 2,
                        py: 0.5,
                        borderRadius: 1,
                        fontSize: '0.7rem',
                        fontWeight: 'bold',
                        boxShadow: 2,
                        zIndex: 1,
                        whiteSpace: 'nowrap'
                      }}
                    >
                      MAIS POPULAR
                    </Box>
                  )}

                  <CardContent sx={{ flexGrow: 1, textAlign: 'center', p: 2 }}>
                    <Box
                      sx={{
                        mb: 1.5,
                        color: getPlanColor(plan.plan_type)
                      }}
                    >
                      {React.cloneElement(getPlanIcon(plan.plan_type), {
                        sx: { fontSize: 40 }
                      })}
                    </Box>

                    <Typography
                      variant='h6'
                      component='div'
                      gutterBottom
                      fontWeight='bold'
                    >
                      {plan.name}
                    </Typography>

                    <Typography
                      variant='h4'
                      fontWeight='bold'
                      gutterBottom
                      sx={{ color: getPlanColor(plan.plan_type) }}
                    >
                      {formatPrice(plan.price)}
                    </Typography>

                    <Chip
                      label={`${plan.duration_days} dias`}
                      size='small'
                      sx={{ mb: 2, height: 24, fontSize: '0.75rem' }}
                    />

                    <Typography
                      variant='body2'
                      display='block'
                      sx={{ mb: 2, minHeight: 40, lineHeight: 1.4 }}
                    >
                      {plan.description}
                    </Typography>

                    <List dense sx={{ textAlign: 'left', mb: 2 }}>
                      <ListItem sx={{ py: 0.5, px: 0 }}>
                        <ListItemIcon sx={{ minWidth: 32 }}>
                          <CheckCircleRounded
                            color='success'
                            sx={{ fontSize: 18 }}
                          />
                        </ListItemIcon>
                        <ListItemText
                          primary='Prioridade na busca'
                          primaryTypographyProps={{
                            variant: 'body2',
                            fontSize: '0.8rem'
                          }}
                        />
                      </ListItem>

                      <ListItem sx={{ py: 0.5, px: 0 }}>
                        <ListItemIcon sx={{ minWidth: 32 }}>
                          {plan.has_premium_badge ? (
                            <CheckCircleRounded
                              color='success'
                              sx={{ fontSize: 18 }}
                            />
                          ) : (
                            <CloseRounded
                              color='disabled'
                              sx={{ fontSize: 18 }}
                            />
                          )}
                        </ListItemIcon>
                        <ListItemText
                          primary='Badge Premium'
                          primaryTypographyProps={{
                            variant: 'body2',
                            fontSize: '0.8rem',
                            color: plan.has_premium_badge
                              ? 'inherit'
                              : 'text.disabled'
                          }}
                        />
                      </ListItem>

                      <ListItem sx={{ py: 0.5, px: 0 }}>
                        <ListItemIcon sx={{ minWidth: 32 }}>
                          {plan.appears_in_home ? (
                            <CheckCircleRounded
                              color='success'
                              sx={{ fontSize: 18 }}
                            />
                          ) : (
                            <CloseRounded
                              color='disabled'
                              sx={{ fontSize: 18 }}
                            />
                          )}
                        </ListItemIcon>
                        <ListItemText
                          primary='Destaque na página inicial'
                          primaryTypographyProps={{
                            variant: 'body2',
                            fontSize: '0.8rem',
                            color: plan.appears_in_home
                              ? 'inherit'
                              : 'text.disabled'
                          }}
                        />
                      </ListItem>
                    </List>

                    <Button
                      variant={
                        plan.plan_type === 'premium' ? 'contained' : 'outlined'
                      }
                      size='medium'
                      fullWidth
                      disabled={creating}
                      onClick={() => handleCreateBoost(plan)}
                      sx={{
                        mt: 'auto',
                        py: 1,
                        fontSize: '0.85rem',
                        fontWeight: 'bold',
                        bgcolor:
                          plan.plan_type === 'premium'
                            ? getPlanColor(plan.plan_type)
                            : undefined,
                        borderColor: getPlanColor(plan.plan_type),
                        color:
                          plan.plan_type === 'premium'
                            ? 'white'
                            : getPlanColor(plan.plan_type),
                        '&:hover': {
                          bgcolor:
                            plan.plan_type === 'premium'
                              ? alpha(getPlanColor(plan.plan_type), 0.8)
                              : undefined,
                          borderColor: getPlanColor(plan.plan_type)
                        }
                      }}
                    >
                      {creating ? 'Processando...' : 'IMPULSIONAR AGORA'}
                    </Button>
                  </CardContent>
                </Card>
              </Grid>
            ))
          ) : (
            <Grid size={{ xs: 12 }}>
              <Typography
                variant='body1'
                textAlign='center'
                color='text.secondary'
              >
                Nenhum plano disponível no momento
              </Typography>
            </Grid>
          )}
        </Grid>
      </DialogContent>

      <DialogActions sx={{ px: 3, pb: 2 }}>
        <Button
          onClick={onClose}
          disabled={creating}
          variant='outlined'
          size='medium'
        >
          Cancelar
        </Button>
      </DialogActions>
    </Dialog>
  )
}

export default BoostPlans
